import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { apiBaseUrl } from '../lib/apiBaseUrl';
import { capture } from '../lib/analytics';
import { saveExtraSeats, type ExtraSeatsResult } from '../lib/extraSeats';
import { log } from '@/lib/logger';
import { useAppSession } from './useAppSession';

/**
 * Save the team's extra-seat count, then refetch billing so the seat row and
 * every invite surface read the new cap. The server re-reads the Stripe
 * subscription and clears its plan cache before it answers, so a plain
 * refetch is fresh; no poll is needed.
 */
export function useExtraSeats(): { save: (extraSeats: number) => Promise<ExtraSeatsResult>; isSaving: boolean } {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const { data: session } = useAppSession();
  const teamId = session?.team?.id;
  const [isSaving, setIsSaving] = useState(false);

  const save = useCallback(
    async (extraSeats: number) => {
      setIsSaving(true);
      try {
        const result = await saveExtraSeats({ apiBase: apiBaseUrl(), teamId, getToken, extraSeats });
        if (result.ok) {
          await queryClient.invalidateQueries({ queryKey: ['billing', 'plan'] });
        } else {
          log.warn('[useExtraSeats] seats not saved', { code: result.code, extraSeats });
          capture('extra_seats_update_failed', { code: result.code, to: extraSeats });
        }
        return result;
      } finally {
        setIsSaving(false);
      }
    },
    [getToken, queryClient, teamId],
  );

  return { save, isSaving };
}
